type AudioProbeEvent = {
  activeIds: Array<number>;
  at: number;
  currentTime: number;
  duration: number | null;
  errorName?: string;
  id: number;
  src: string;
  type: string;
};

type AudioProbeRecord = {
  createdAt: number;
  currentTime: number;
  duration: number | null;
  element: HTMLAudioElement;
  id: number;
  lastEvent: string;
  paused: boolean;
  playAttempts: number;
  playRejections: number;
  playResolutions: number;
  src: string;
  srcRemoved: boolean;
};

type AudioProbeState = {
  events: Array<AudioProbeEvent>;
  records: Array<AudioProbeRecord>;
  recordsByElement: WeakMap<HTMLAudioElement, AudioProbeRecord>;
};

const enabled = import.meta.env.VITE_AUDIO_E2E_PROBE === "1";
let nextId = 0;
const state: AudioProbeState = {
  events: [],
  records: [],
  recordsByElement: new WeakMap(),
};

function activeIds(): Array<number> {
  return state.records
    .filter(({ element }) => !element.paused && !element.ended)
    .map(({ id }) => id)
    .sort((left, right) => left - right);
}

function recordEvent(record: AudioProbeRecord, type: string, errorName?: string): void {
  const duration = Number.isFinite(record.element.duration) ? record.element.duration : null;
  record.currentTime = Number.isFinite(record.element.currentTime) ? record.element.currentTime : 0;
  record.duration = duration;
  record.lastEvent = type;
  record.paused = record.element.paused;
  record.src = record.element.currentSrc || record.element.src || "";
  state.events.push({
    activeIds: activeIds(),
    at: performance.now(),
    currentTime: record.currentTime,
    duration,
    errorName,
    id: record.id,
    src: record.src,
    type,
  });
}

function getRecord(element: HTMLAudioElement): AudioProbeRecord | null {
  if (!enabled) return null;
  const existing = state.recordsByElement.get(element);
  if (existing) return existing;

  const record: AudioProbeRecord = {
    createdAt: performance.now(),
    currentTime: Number.isFinite(element.currentTime) ? element.currentTime : 0,
    duration: Number.isFinite(element.duration) ? element.duration : null,
    element,
    id: ++nextId,
    lastEvent: "created",
    paused: element.paused,
    playAttempts: 0,
    playRejections: 0,
    playResolutions: 0,
    src: element.currentSrc || element.src || "",
    srcRemoved: false,
  };
  state.records.push(record);
  state.recordsByElement.set(element, record);

  for (const eventName of [
    "abort",
    "canplay",
    "emptied",
    "ended",
    "error",
    "loadedmetadata",
    "loadstart",
    "pause",
    "playing",
    "stalled",
    "suspend",
    "timeupdate",
    "waiting",
  ]) {
    element.addEventListener(eventName, () => {
      if (eventName === "emptied" && element.getAttribute("src") === null) record.srcRemoved = true;
      recordEvent(record, eventName);
    });
  }

  recordEvent(record, "created");
  return record;
}

function installProbeApi(): void {
  if (!enabled || typeof window === "undefined" || window.__audioProbe) return;

  Object.defineProperty(window, "__audioProbe", {
    configurable: false,
    value: {
      seekMostRecentlyPlayedToEnd(leadSeconds = 0.35): boolean {
        const record = [...state.records].reverse().find((candidate) => candidate.playAttempts > 0);
        if (!record || !Number.isFinite(record.element.duration) || record.element.duration <= leadSeconds) {
          return false;
        }
        record.element.currentTime = record.element.duration - leadSeconds;
        recordEvent(record, "probe-seek-to-end");
        return true;
      },
      snapshot() {
        return {
          events: state.events.map((event) => ({ ...event, activeIds: [...event.activeIds] })),
          records: state.records.map(({ element: _element, ...record }) => ({ ...record })),
        };
      },
    },
  });
}

installProbeApi();

export function observeAudioForE2e(element: HTMLAudioElement): void {
  if (!enabled) return;
  installProbeApi();
  getRecord(element);
}

export function recordAudioPlayAttemptForE2e(element: HTMLAudioElement): void {
  const record = getRecord(element);
  if (!record) return;
  record.playAttempts += 1;
  recordEvent(record, "play-attempt");
}

export function recordAudioPlayResolutionForE2e(element: HTMLAudioElement): void {
  const record = getRecord(element);
  if (!record) return;
  record.playResolutions += 1;
  recordEvent(record, "play-resolved");
}

export function recordAudioPlayRejectionForE2e(element: HTMLAudioElement, error: unknown): void {
  const record = getRecord(element);
  if (!record) return;
  record.playRejections += 1;
  const errorName = error instanceof DOMException || error instanceof Error ? error.name : String(error);
  recordEvent(record, "play-rejected", errorName);
}

declare global {
  interface Window {
    __audioProbe?: {
      seekMostRecentlyPlayedToEnd: (leadSeconds?: number) => boolean;
      snapshot: () => unknown;
    };
  }
}
